import {useLazyQuery} from "@apollo/client/react";
import {CtrDomainsVf, CtrStaticTables, CtrValeursFixes, Maybe, Query} from "../graphql/generated/graphql-main.tsx";
import {gqlTemplates} from "../utils/gql-client.ts";
import {useWpContext} from "../components/context/WordPressProvider.tsx";
import {findBy} from "../utils/array-utils.ts";
import {useXDispatch, useXSelector} from "../store/hooks.ts";
import {coreActions} from "../store/slice-core";
import {useEffect} from "react";
import {CoreStateType} from "../store/slice-core/types.ts";
import {DocumentNode} from "@apollo/client";

const set = coreActions.$setValeursFixes;
// data type in Redux Store core slice
const dtype: keyof CoreStateType = CtrStaticTables.ValeursFixes;
// GraphQL document node to query for the data type
const gnode: DocumentNode = gqlTemplates.valeursFixes;
// GraphQL mutation result data key
const gkey: keyof Query = "ctrValeursFixes";

export default function useCtrValeursFixes<T extends CtrValeursFixes>() {
    const dispatch = useXDispatch();
    const {is_fr} = useWpContext();
    const $entries = useXSelector(s => s.core[dtype] as T[]);

    const [fetch, {loading}] = useLazyQuery<Query>(gnode, {fetchPolicy: "cache-and-network"});
    const load = async () => {
        const {data} = await fetch();
        if (data?.[gkey] == null) return;
        dispatch(set(data?.[gkey] as T[]));
    }

    // get all values for the specified domain
    const valuesByDomain = (domain: CtrDomainsVf) => {
        return $entries.filter(it => it?.DOMAINE == domain);
    }

    // easily get EN|FR label given proper code and domain for that code
    const fixedValue = (domain: CtrDomainsVf, code?: Maybe<string>, args?: {
        strict?: boolean,
    }): Maybe<string> => {
        const _values = valuesByDomain(domain);
        const _value = findBy(_values, "VALEUR", code);
        const _key1: keyof CtrValeursFixes = is_fr ? "CUSTOM_FR" : "CUSTOM_EN";
        const _key2: keyof CtrValeursFixes = is_fr ? "DESCRIPTION_FRANCAISE" : "DESCRIPTION_ANGLAISE";

        // return value or 'null' if empty string
        if (args?.strict)
            return _value?.[_key2] || null;
        else
            return _value?.[_key1] || _value?.[_key2] || null;
    }

    useEffect(() => {
        if ($entries.length > 0) return;
        load();
    }, [$entries]);

    return {
        $entries,
        loading,
        fixedValue
    }
}